import React, { useEffect, useState } from 'react';
import './ChevronProcessDiagram.scss';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { ChevronDiagramStage } from './models/ChevronDiagramStage';
import { STAGE_ITEMS } from '../Constants/dropdownItems';
import { ChevronDiagramColumn } from './ChevronDiagramColumn';
import { ChevronDiagramEvent } from './models/ChevronDiagramEvent';
import Helpers from '../../../utils/helper';
import { useStateSelector } from '../../../store/selectors';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { USER_ROLES } from '../../../utils/constants';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
interface ChevronProcessDiagramProps {
    timelineEvents: TimelineEvent[];
    dataRequestProjects?: LinkedDataRequestProject[];
    engagementStartDate: Date;
    onActionClick(item: ChevronDiagramEvent): void;
    isEventActionVisible: (eventId: string) => boolean;
    isTempoPreview?: boolean;
    engagementId: number;
    isBuyerView: boolean;
}

export const ChevronProcessDiagram = (props: ChevronProcessDiagramProps) => {
    const [chevronDiagramStages, setChevronDiagramStages] =
        useState<ChevronDiagramStage[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const party = useCurrentMarketplaceParty(props.engagementId);

    useEffect(() => {
        setChevronDiagramStages(groupTimelineEvents(props.timelineEvents));
        setIsLoading(false);
    }, [props.timelineEvents]);

    const convertToChevronDiagramEvent = (
        event: TimelineEvent
    ): ChevronDiagramEvent => {
        const project =
            party !== TimelineParty.Buyer &&
            props.dataRequestProjects
                ? props.dataRequestProjects?.find(
                      (p) => p.id == event.linkedAction?.referenceItemId
                  )
                : undefined;

        return {
            id: event.id,
            name: event.name,
            party: event.party,
            dayStart: calculateDateInMilliseconds(
                props.engagementStartDate,
                event.dayStart
            ),
            dayEnd: calculateDateInMilliseconds(
                props.engagementStartDate,
                event.dayEnd
            ),
            dayCompleted: calculateDateInMilliseconds(
                props.engagementStartDate,
                event.dayCompleted
            ),
            status: event.status,
            linkedAction: event.linkedAction,
            actionLinkText: project?.actionLinkText,
            isSuspendEvent: event.isSuspendEvent,
        };
    };

    const calculateDateInMilliseconds = (
        baseDate: Date,
        daysToAdd: number
    ): Date => {
        if (typeof baseDate === 'string') {
            baseDate = new Date(baseDate);
        }
        return new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    };

    const groupTimelineEvents = (
        events: TimelineEvent[]
    ): ChevronDiagramStage[] => {
        const stageMap = new Map<number, ChevronDiagramStage>();

        events.forEach((event) => {
            const { stage, stageName } = event;

            if (!stageMap.has(stage)) {
                const index = stageMap.size;
                const color =
                    index < STAGE_ITEMS.length
                        ? STAGE_ITEMS[index].color
                        : null;

                stageMap.set(stage, {
                    name: stageName,
                    backgroundColor: color,
                    events: [],
                });
            }

            stageMap
                .get(stage)!
                .events.push(convertToChevronDiagramEvent(event));
        });

        return Array.from(stageMap.values());
    };

    return (
        <div className="chevron-process-diagram">
            <div id="phases">
                {isLoading ? (
                    Helpers.renderPageLoader()
                ) : (
                    <ul>
                        {chevronDiagramStages.map((stage, index) => (
                            <li>
                                <ChevronDiagramColumn
                                    chevronDiagramStage={stage}
                                    onActionClick={props.onActionClick}
                                    isEventActionVisible={
                                        props.isEventActionVisible
                                    }
                                    party={party}
                                    isTempoPreview={props.isTempoPreview}
                                    engagementId={props.engagementId}
                                    isBuyerView={props.isBuyerView}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
